import Injector from 'vue-inject';
import Debug from '@/util/debug';
import * as LocationTypes from '@/models/target-location';
import { IDB_TABLE_LOCATIONS } from '@/util/constants';
import idb from '@/util/indexed-db';

const debug = Debug('service:Locations');

export default class LocationsService {
  constructor(adApiService, gcs, gms, oms) {
    this.adApiService = adApiService;
    this.genericConfigService = gcs;
    this.googleMapsService = gms;
    this.offlineModeService = oms;
    this.locationsCache = {};
  }

  autocompleteLocations(search, types, countryCode) {
    const online = this.offlineModeService.getStatus();
    const typeArray = Array.isArray(types) ? types : [types];
    const cacheKey = `${typeArray.join(',')}:${search}:${countryCode}:${online}`;

    if (cacheKey in this.locationsCache) {
      debug.info('locations for %s from cache: %o', cacheKey, this.locationsCache[cacheKey]);
      return new Promise(resolve => resolve(this.locationsCache[cacheKey]));
    }

    const promise = !online
      ? this.autocompleteOfflineLocations(search, 10, typeArray, countryCode)
      : this.adApiService.autocompleteLocations(search, 10, typeArray, countryCode);

    return promise
      .then(res => {
        const filtered = res.filter(i => i.type !== 'subcity' && i.type !== 'neighborhood');

        this.locationsCache[cacheKey] = filtered;
        debug.info('locations for %s from api: %o, filtered to: %o', cacheKey, res, filtered);
        return filtered;
      });
  }

  // eslint-disable-next-line class-methods-use-this
  autocompleteOfflineLocations(search, count, types, countryCode) {
    return idb[IDB_TABLE_LOCATIONS]
      .where('name').startsWithIgnoreCase(search)
      .filter(l => types.indexOf(l.type) >= 0 && l.country_code === countryCode)
      .limit(count)
      .toArray();
  }

  getMetadata(locations) {
    if (!this.offlineModeService.getStatus()) {
      return this.metadataOffline(locations);
    }
    return this.metadataFacebook(locations);
  }

  // eslint-disable-next-line class-methods-use-this
  metadataOffline(locations) {
    const locationFbIds = [locations.facebookId || -1, locations.key || -1];
    return idb[IDB_TABLE_LOCATIONS]
      .where('fb_city_id')
      .anyOf(locationFbIds)
      .toArray()
      .then(res => (res.length === 1 ? res[0] : res));
  }

  metadataFacebook(locations) {
    const typeToResKey = {
      [LocationTypes.LOCATION_TYPE_CITY]: 'cities',
      [LocationTypes.LOCATION_TYPE_REGION]: 'regions',
      [LocationTypes.LOCATION_TYPE_COUNTRY]: 'countries',
      [LocationTypes.LOCATION_TYPE_ZIP]: 'zips',
    };

    const locationsArr = Array.isArray(locations) ? locations : [locations];

    return this.adApiService.locationsMetadata(
      locationsArr.filter(l => l.type === LocationTypes.LOCATION_TYPE_CITY).map(l => l.key),
      locationsArr.filter(l => l.type === LocationTypes.LOCATION_TYPE_REGION).map(l => l.key),
      locationsArr.filter(l => l.type === LocationTypes.LOCATION_TYPE_COUNTRY).map(l => l.key),
      locationsArr.filter(l => l.type === LocationTypes.LOCATION_TYPE_ZIP).map(l => l.key),
    )
      .then(res => {
        const result = [];
        locationsArr.forEach(loc => {
          let metadata = null;
          try {
            const resKey = typeToResKey[loc.type];
            const locKey = String(loc.key).toUpperCase(); // keys can't have lowercase
            metadata = res[resKey][locKey];
          } catch (e) {
            metadata = null;
          }
          result.push(metadata);
        });
        return result;
      })
      .then(r => {
        const uniqueArr = r.filter((v, i, a) => a.indexOf(v) === i);
        if (uniqueArr.length === 1 && uniqueArr[0] === null) {
          throw new Error('Could not get metadata');
        } else {
          return r;
        }
      })
      // if input was single location, return single coords
      .then(res => (Array.isArray(locations) ? res : res[0]));
  }

  coordinatesFacebook(locations) {
    return this.getMetadata(locations)
      .then(res => (Array.isArray(res) ? res : [res]))
      .then(locs => locs.map(loc => (loc ? {
        latitude: loc.latitude,
        longitude: loc.longitude,
      } : null)))
      .then(res => (Array.isArray(locations) ? res : res[0]));
  }

  // eslint-disable-next-line class-methods-use-this
  autocompleteGoogle(search, countryCode) {
    return this.googleMapsService.initialize()
      .then(() => {
        const places = (window.google && window.google.maps) ? window.google.maps.places : null;
        if (!places) {
          debug.warn('error: No window.google.maps.places object');
        }

        return new Promise((resolve, reject) => {
          const service = new places.AutocompleteService();

          service.getPlacePredictions({
            input: search,
            types: ['geocode'],
            componentRestrictions: {
              country: countryCode,
            },
          }, (predictions, status) => {
            if (status !== places.PlacesServiceStatus.OK
              && status !== places.PlacesServiceStatus.ZERO_RESULTS
            ) {
              return reject(predictions);
            }

            return resolve((predictions || []).map(p => ({
              name: p.description,
              place_id: p.place_id,
              reference: p.reference,
              type: LocationTypes.LOCATION_TYPE_ADDRESS,
            })));
          });
        });
      });
  }

  // eslint-disable-next-line class-methods-use-this
  metadataGoogle(loc) {
    return this.googleMapsService.initialize()
      .then(() => {
        const places = (window.google && window.google.maps) ? window.google.maps.places : null;
        if (!places) {
          debug.warn('error: No window.google.maps.places object');
        }

        return new Promise((resolve, reject) => {
          // TODO use real element for attributions
          const service = new places.PlacesService(document.createElement('div'));
          // eslint-disable-next-line consistent-return
          service.getDetails({ placeId: loc.place_id }, (placeById, statusById) => {
            if (statusById === places.PlacesServiceStatus.OK) {
              return resolve(placeById);
            }

            service.getDetails({ reference: loc.reference }, (placeByRef, statusByRef) => {
              if (statusByRef === places.PlacesServiceStatus.OK) {
                return resolve(placeByRef);
              }

              return reject('status');
            });
          });
        });
      });
  }

  getLocationsTypesFromApi() {
    debug.info('getLocationsFromApi');
    return this.genericConfigService.getConfig()
      .then(config => this.availableLocationTypes = config.locations)
      .catch((err) => {
        debug.error('Could not load location types', err);
        this.availableLocationTypes = [];
        return this.availableLocationTypes;
      });
  }

  getLocationsTypes() {
    this.genericConfigService.getConfig().then((config) => {
      this.availableLocationTypes = config.locations;
    });
  }
}

Injector.service('locationsService', [
  'adApiService',
  'genericConfigService',
  'googleMapsService',
  'offlineModeService',
], LocationsService);



// WEBPACK FOOTER //
// ./src/services/LocationsService.js